import TTLCache from '@isaacs/ttlcache';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchHolterBeatEventsCount } from '@/entities/beat';
import {
  fetchHolterEvents,
  fetchHolterRhythmEventsCount,
  useReadEventsInfoRhythmEvents,
  formatEventStrip,
  generateFilterSpecialEventType,
  isAppliedFilterEvent,
} from '@/entities/event';
import {
  EMITTER_CONSTANTS,
  canAvgRhythmEvents,
  canShowCountBeatEvents,
  canShowDurationEvents,
  ECG_EVENT_MENU_OPTIONS,
  EVENT_COUNT_CONVERT,
  KEY_CANCEL,
  KEY_RECOIL,
  LIST_RUN_EVENT,
  STRIP_EVENT_THUMBNAIL_INFO,
} from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import {
  RHYTHM_EVENT_TAB_ID,
  selectEventFilter,
  selectHolterEvents,
  selectEventChanges,
  selectIsActiveTab,
  selectIsLoadingStrip,
  selectPageIndex,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { getStripIndex, getStripPosition } from '@/entities/strip';
import { logError } from '@/shared/lib/apiHelpers';
import { getCachedDataSlice, getCurrentChunk } from './handler';
import type { StripEventsProps } from './stripEvents';

// Initialize cache with a time-to-live of 1 hour
const stripEventsCache = new TTLCache({ ttl: 3600000, max: 3 });

/**
 * Supplies the strip thumbnail grid of the rhythm-events tab with the current page of holter events for the active event type. Builds the server filter from the special event types and the tab filter, serves pages out of a chunked cache while prefetching neighbouring chunks, corrects counts for locally changed events, keeps the selected and multi-selected strips in the report store, and moves the selection with the arrow keys.
 *
 * @param props - Active event button, study identifiers, timezone offset, report start and the ECG data map.
 * @returns An object carrying the tab key, chart inputs, the loaded holter events, loading and selection state, the selection handlers and the thumbnail display config.
 */
const useStripEvents = ({
  activeButton,
  studyId,
  profileId,
  timezoneOffset,
  startReport,
  ecgDataMap,
}: StripEventsProps) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Toggles the loading state of the strip grid. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the strip pagination of this tab to another page. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new page index value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const sizeThumbnailConfig = useReportStore((s) => s.sizeEventThumbnailConfig);
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const specialEventTypes = useReportStore((s) => s.specialEventTypes);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);
  /**
   * Reads the locally pending event changes recorded for the rhythm-events tab. Evaluates the tab-keyed selector against the current report store state instead of subscribing to it.
   *
   * @returns The changed events held for the tab.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Stores the strips picked out by the drag-selection area. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const setSpecialEventCountAction = useReportStore((s) => s.actions.setSpecialEventCount);
  /**
   * Stores the filtered event count for the active event type. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new count value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSpecialEventCount = (v: Parameters<typeof setSpecialEventCountAction>[1]) =>
    setSpecialEventCountAction(keyRecoil, v);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores the strip currently selected in the grid. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const setHolterEventsAction = useReportStore((s) => s.actions.setHolterEvents);
  /**
   * Stores the page of holter events shown in the strip grid. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setHolterEvents = (v: Parameters<typeof setHolterEventsAction>[1]) =>
    setHolterEventsAction(keyRecoil, v);
  const getEventsInfo = useReadEventsInfoRhythmEvents();
  const setSelectedEventInfoIndexAction = useReportStore(
    (s) => s.actions.setSelectedEventInfoIndex,
  );
  /**
   * Selects which event-info entry is shown alongside the current strip. Forwards the value or updater to the store action together with the tab key.
   *
   * @param v - The new index value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedEventInfoIndex = (v: Parameters<typeof setSelectedEventInfoIndexAction>[1]) =>
    setSelectedEventInfoIndexAction(keyRecoil, v);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const [selected, setSelected] = useState(new Set<string>());
  const selectionAreaWrapperRef = useRef<any>();
  const stripPosition = useRef({ x: 0, y: 0 });
  const prevPageIndex = useRef(pageIndex.index);
  const arrowKeyToPrevPage = useRef(false);
  const timeoutRef = useRef<any>();
  const isMounted = useRef(true);
  const cache = useRef(new Map()); // Create a cache
  let isActiveQuery: boolean;

  // Fetch background data for caching
  /**
   * Prefetches whole chunks of holter events into the strip cache so page turns do not wait on the API. Does nothing without chunks to fill, otherwise requests each chunk in parallel with a skip and take window bounded by the total strip count, merges the responses into the cached events under their chunk keys, and on failure clears the in-flight marker and logs the error.
   *
   * @param holterEventsFilter - Base query filter the per-chunk skip is applied to.
   * @param totalStrip - Total event count used to bound the last chunk's take.
   * @param chunkSize - Number of events per chunk.
   * @param cacheChunks - Indices of the chunks to fetch.
   * @param cacheKey - Cache entry key for this event type and filter combination.
   * @param _cacheKey - In-flight marker key cleared when the fetch fails.
   * @param lastResult - Previously cached payload merged with the new chunks.
   * @returns A promise resolving once the requested chunks have been cached.
   */
  const fetchBackgroundData = async ({
    holterEventsFilter,
    totalStrip,
    chunkSize,
    cacheChunks,
    cacheKey,
    _cacheKey,
    lastResult = {},
  }: any) => {
    if (_.isEmpty(cacheChunks)) return;
    try {
      const getCached = (await stripEventsCache.get(cacheKey)) || lastResult || {};
      const stripCacheData = {
        events: getCached.events || {},
        chunks: [...(getCached?.chunks || []), ...(cacheChunks || [])],
      };
      const promisses: any[] = [];
      _.forEach(cacheChunks, (chunk) => {
        const skip = chunk * chunkSize;
        const take = Math.min(chunkSize, totalStrip - skip);
        if (take <= 0) {
          return;
        }
        const filter = {
          ...holterEventsFilter,
          skip,
        };

        promisses.push(fetchHolterEvents(filter, take, true, KEY_CANCEL.API_HOLTER_AI, false));
      });
      const results = await Promise.all(promisses);

      _.forEach(results, (result: any, idx) => {
        _.assign(stripCacheData.events, {
          [`chunk-${cacheChunks[idx]}`]: result?.events || [],
        });
      });
      stripEventsCache.set(cacheKey, stripCacheData);
    } catch (err) {
      cache.current.set(_cacheKey, false);
      // pre-existing: logError(msg, error) called with just 1 arg here, error is undefined.
      logError(err as any, undefined);
    }
  };

  /**
   * Stops any further background prefetching of strip event chunks for this mount. Clears the mounted ref flag that serves as the cancellation signal.
   *
   * @returns Nothing.
   */
  const cancelFetchBackgroundData = () => {
    isMounted.current = false; // Đặt cờ để hủy các yêu cầu fetch
  };

  const handleCacheData = ({
    holterEventsFilter,
    totalStrip,
    chunkSize,
    currentChunk,
    lastResult,
    cacheKey,
  }: any) => {
    const { chunks } = lastResult || {};
    const hasCurrnetChunk = chunks?.includes(currentChunk);
    const hasNextCurrentChunk = chunks?.includes(currentChunk + 1);
    const hasPrevCurrentChunk = chunks?.includes(currentChunk - 1);
    if (hasCurrnetChunk && hasNextCurrentChunk && hasPrevCurrentChunk) {
      return;
    }
    const cacheChunks: any[] = [];
    if (!hasPrevCurrentChunk && currentChunk - 1 >= 0) {
      cacheChunks.push(currentChunk - 1);
    }
    if (!hasCurrnetChunk) {
      cacheChunks.push(currentChunk);
    }
    if (!hasNextCurrentChunk) {
      cacheChunks.push(currentChunk + 1);
    }

    const _cacheKey = `_cacheKey-${cacheKey}-${currentChunk}`;
    if (!_.isEmpty(cacheChunks) && !cache.current.has(_cacheKey)) {
      cache.current.set(_cacheKey, true);
      fetchBackgroundData({
        holterEventsFilter,
        totalStrip,
        chunkSize,
        cacheChunks,
        cacheKey,
        _cacheKey,
        lastResult,
      });
    }
  };

  /**
   * Loads the current page of holter events for the active event type into the strip grid. Composes the special-event and filter criteria, fetches the filtered count when a filter is applied, slices from the chunked cache when it covers the page or otherwise queries the API, schedules background caching of the neighbouring chunks, removes locally changed events and backfills the page from the next cursor, then stores the formatted events and resolves which strip stays selected.
   *
   * @returns A promise resolving once the page of events and the selection have been written to the store.
   */
  const fetchEvents = async () => {
    const chunkSize = 100;
    setIsLoadingStrip(true);
    try {
      let downloadData: any[] = [];
      const type = activeButton;
      const additionalFilter = generateFilterSpecialEventType(type, specialEventTypes);
      const isAppliedFilter = isAppliedFilterEvent(eventFilter, type);
      if (isAppliedFilter) {
        _.assign(additionalFilter, {
          ...(eventFilter.isHideReviewed && { isReviewed: false }),
          ...(eventFilter.isCapture && { isCaptured: true }),
          ...(eventFilter.isAvgHrBelow100 &&
            LIST_RUN_EVENT.includes(type) && { isAvgHrBelow100: true }),
          ...(eventFilter.sortOrder &&
            (canShowCountBeatEvents.includes(type) ||
              canShowDurationEvents.includes(type) ||
              canAvgRhythmEvents.includes(type)) && {
              sortBy: eventFilter.sortBy || 'countBeats',
              sortOrder: eventFilter.sortOrder,
            }),
        });
      }
      let specialEventCount: any = 0;
      const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;
      const holterEventsFilter = {
        studyId,
        profileId,
        types: [type],
        skip: validatePage * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit,
        ...additionalFilter,
      };

      if (!_.isEmpty(additionalFilter)) {
        const holterEventsCountFilter = {
          studyId,
          profileId,
          types: [type],
          ...additionalFilter,
        };
        specialEventCount =
          _.find(ECG_EVENT_MENU_OPTIONS, (x) => x.value === type).type === 'beat'
            ? await fetchHolterBeatEventsCount(
                holterEventsCountFilter,
                true,
                KEY_CANCEL.API_HOLTER_AI,
              )
            : await fetchHolterRhythmEventsCount(
                holterEventsCountFilter,
                true,
                KEY_CANCEL.API_HOLTER_AI,
              );
      }

      const startIndex = validatePage * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit;
      const currentChunk = getCurrentChunk(startIndex, chunkSize);
      const keyString = JSON.stringify({ type, additionalFilter });
      const cacheKey = `stripEventsCache-${keyString}`;
      const currentCacheData: any = stripEventsCache.has(cacheKey)
        ? stripEventsCache.get(cacheKey)
        : {};
      const endIndex = (validatePage + 1) * STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit;
      const isChunkOverPage =
        Math.floor(endIndex / chunkSize) - Math.floor(startIndex / chunkSize) > 0;
      const canUseCache = !isChunkOverPage || currentCacheData?.chunks?.includes(currentChunk + 1);
      const totalStrip = (getEventsInfo() as any)[(EVENT_COUNT_CONVERT as any)[type]]?.count;
      if (
        stripEventsCache.has(cacheKey) &&
        currentCacheData?.chunks?.includes(currentChunk) &&
        canUseCache
      ) {
        // Apply the filter before slicing the data
        const { events }: any = stripEventsCache.get(cacheKey);
        const slicedData = getCachedDataSlice(
          events,
          startIndex,
          endIndex,
          currentChunk,
          chunkSize,
        );
        downloadData = [...slicedData];
      } else {
        const { events } = await fetchHolterEvents(
          holterEventsFilter,
          STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit,
          true,
          KEY_CANCEL.API_HOLTER_AI,
          false,
        );
        downloadData = [...events];
      }

      if (totalStrip) {
        handleCacheData({
          holterEventsFilter,
          totalStrip,
          chunkSize,
          currentChunk,
          lastResult: currentCacheData,
          cacheKey,
        });
      }

      if (isAppliedFilter && downloadData.length) {
        let countRemoveInList = 0;
        let countRemoveOutsideList = 0;
        let hasRemoveEventInList = false;
        //* Nếu filter event hide review thì ko hiển thị event change từ review false thành true
        //* Nếu filter event captured thì ko hiển thị event có captured lúc đầu nhưng xoá đi
        getEventChanges().forEach((eventChange: any) => {
          if (eventChange?.type === type) {
            hasRemoveEventInList = false;
            _.remove(downloadData, (x) => {
              // Remove in list
              if (
                x.id === eventChange.idEvent &&
                ((eventFilter.isHideReviewed && eventChange.isReviewed) ||
                  (eventFilter.isCapture &&
                    eventChange.isUpdateImage &&
                    _.isEmpty(eventChange.stripImgs)))
              ) {
                hasRemoveEventInList = true;
                countRemoveInList += 1;
                return true;
              }
              return false;
            });
            if (
              !hasRemoveEventInList &&
              ((eventFilter.isHideReviewed && eventChange.isReviewed) ||
                (eventFilter.isCapture &&
                  eventChange.isUpdateImage &&
                  _.isEmpty(eventChange.stripImgs)))
            ) {
              // Remove outside list for correct total event count
              countRemoveOutsideList += 1;
            }
          }
        });
        if (countRemoveInList > 0) {
          const filterFetchMoreHolterEvent = {
            studyId,
            profileId,
            types: [type],
            cursor: downloadData[downloadData.length - 1].start,
            ...additionalFilter,
          };
          const { events: downloadedHolterEventsMore } = await fetchHolterEvents(
            filterFetchMoreHolterEvent,
            countRemoveInList,
            true,
            KEY_CANCEL.API_HOLTER_AI,
          );
          downloadData = [...downloadData, ...downloadedHolterEventsMore];
        }
        const formatType = (EVENT_COUNT_CONVERT as any)[type];
        if (specialEventCount?.[formatType]) {
          specialEventCount[formatType].count -= countRemoveInList + countRemoveOutsideList;
          if (specialEventCount?.[formatType]?.count < 0) {
            specialEventCount[formatType].count = 0;
          }
        }
      }
      if (specialEventCount) {
        setSpecialEventCount(specialEventCount as any);
      }
      const newHolterEvents = formatEventStrip(downloadData);

      if (isActiveQuery) {
        // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing isNew/idEvent on some members - atom-type gap.
        const selectedStripAny = selectedStrip as any;
        if (!selectedStripAny?.isNew) {
          if (validatePage < prevPageIndex.current && arrowKeyToPrevPage.current) {
            stripPosition.current = getStripPosition(
              newHolterEvents.length - 1,
              STRIP_EVENT_THUMBNAIL_INFO.stripPerRow,
            );
            setSelectedStrip(newHolterEvents[newHolterEvents.length - 1] || null);
          } else {
            const selectedStripIndex = !isAppliedFilter
              ? _.findIndex(newHolterEvents, (x) => x.idEvent === selectedStripAny?.idEvent)
              : -1;
            const newSelectedStrip =
              newHolterEvents[selectedStripIndex] || newHolterEvents[0] || null;
            stripPosition.current = getStripPosition(
              selectedStripIndex === -1 ? 0 : selectedStripIndex,
              STRIP_EVENT_THUMBNAIL_INFO.stripPerRow,
            );
            setSelectedStrip((prev: any) => {
              if (!_.isEqual(newSelectedStrip, prev)) {
                return newSelectedStrip;
              }
              return prev;
            });
          }
          arrowKeyToPrevPage.current = false;
          prevPageIndex.current = validatePage;
        }
        setHolterEvents(newHolterEvents as any);
        setIsLoadingStrip(false);
      }
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
  };

  /**
   * Replaces the set of strip keys picked out by the drag-selection area. Passes the new set to local state, which an effect maps back onto the matching holter events.
   *
   * @param newSelected - The new set of selected strip keys.
   * @returns Nothing.
   */
  const setSelectedEvent = (newSelected: any) => {
    setSelected(newSelected);
  };

  useEffect(() => {
    const selectedArr = [...selected];
    const selectedEvents: any[] = [];
    _.forEach(selectedArr, (item: any) => {
      const id = item.split('-')[1];
      /**
       * Resolves a strip key coming from the drag selection back to its holter event. Scans the currently loaded events for one whose idEvent equals the id parsed out of the key.
       *
       * @returns The matching holter event, or undefined when none is loaded.
       */
      const selectedEvent = (holterEvents || []).find((event: any) => event.idEvent === id);
      if (selectedEvent) {
        selectedEvents.push(selectedEvent);
      }
    });
    setSelectedMultipleStrip((prev: any) => {
      if (prev.length === 0 && selectedEvents.length === 0) {
        return prev;
      }
      return selectedEvents;
    });
  }, [selected, holterEvents]);

  /**
   * Selects a strip thumbnail clicked in the grid. Resets the event-info index and, only when the clicked event differs from the current one, records its row and column position and makes it the selected strip.
   *
   * @param event - The holter event behind the clicked thumbnail.
   * @param index - Position of the thumbnail in the current page, used to track the grid cursor.
   * @returns Nothing.
   */
  const handleClickSelectEvent = useCallback((event: any, index: any) => {
    setSelectedEventInfoIndex(0);
    setSelectedStrip((prev: any) => {
      if (event?.idEvent !== prev?.idEvent) {
        stripPosition.current = getStripPosition(index, STRIP_EVENT_THUMBNAIL_INFO.stripPerRow);
        return event;
      }
      return prev;
    });
  }, []);

  /**
   * Drops every cached page of strip events. Clears both the shared strip event cache and the local map of in-flight chunk markers.
   *
   * @returns Nothing.
   */
  const resetCacheStripEvents = () => {
    stripEventsCache.clear();
    cache.current.clear();
  };

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton, timeoutRef.current);
      cancelFetchBackgroundData();
      resetCacheStripEvents();
    },
    [activeButton],
  );

  useEffect(() => {
    resetCacheStripEvents();
  }, [isRenderViewerEcg]);

  useEffect(() => {
    isActiveQuery = true;
    if (isRenderViewerEcg) {
      fetchEvents();
    }
    if (isActiveTab) {
      setSelected(new Set());
      selectionAreaWrapperRef.current.resetSelection();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [activeButton, specialEventTypes, eventFilter, pageIndex, isActiveTab, isRenderViewerEcg]);

  useEffect(() => {
    /**
     * Moves the strip selection around the thumbnail grid with the arrow keys. Ignores auto-repeats, presses while loading and presses outside the hovered rhythm-events tab zone, shifts the tracked row and column, selects the strip landing under it, and emits the next or previous page event when the move runs past either end of the page.
     *
     * @param event - The keydown event whose key decides the direction.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (_.isEmpty(selectionAreaWrapperRef.current) || event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(RHYTHM_EVENT_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      event.preventDefault();
      const { key } = event;
      switch (key) {
        case 'ArrowRight': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y += 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_EVENT_THUMBNAIL_INFO.stripPerRow,
          );
          if (index > holterEvents.length - 1) {
            stripPosition.current.y -= 1;
            emitter.emit(EMITTER_CONSTANTS.RHYTHM_EVENTS_NEXT);
          } else {
            const event = holterEvents[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y -= 1;
            }
          }
          break;
        }
        case 'ArrowLeft': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y -= 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_EVENT_THUMBNAIL_INFO.stripPerRow,
          );
          if (index < 0) {
            arrowKeyToPrevPage.current = true;
            stripPosition.current.y += 1;
            emitter.emit(EMITTER_CONSTANTS.RHYTHM_EVENTS_PREV);
          } else {
            const event = holterEvents[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y += 1;
            }
          }
          break;
        }
        case 'ArrowUp': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x -= 1;
          const event =
            holterEvents[
              getStripIndex(stripPosition.current, STRIP_EVENT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x += 1;
          }
          break;
        }
        case 'ArrowDown': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x += 1;
          const event =
            holterEvents[
              getStripIndex(stripPosition.current, STRIP_EVENT_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x -= 1;
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [holterEvents, isLoadingStrip]);

  useUpdateEffect(() => {
    const { x: row, y: column } = stripPosition.current;
    const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;
    const currentStripIndexInList = column + row * STRIP_EVENT_THUMBNAIL_INFO.prevStripPerRow;
    const currentEventIndex =
      currentStripIndexInList + validatePage * STRIP_EVENT_THUMBNAIL_INFO.prevStripDisplayLimit;
    const newPage = Math.floor(currentEventIndex / STRIP_EVENT_THUMBNAIL_INFO.stripDisplayLimit);
    setPageIndex({ index: newPage < 0 ? 0 : newPage });
  }, [sizeThumbnailConfig]);
  const isHasSelected = useMemo(
    () =>
      !_.isEmpty(selectedMultipleStrip) ||
      (!_.isEmpty(selectedStrip) && !(selectedStrip as any).isNew),
    [selectedMultipleStrip, selectedStrip],
  );

  return {
    keyRecoil,
    activeButton,
    timezoneOffset,
    startReport,
    ecgDataMap,
    selectionAreaWrapperRef,
    holterEvents,
    isLoadingStrip,
    selectedStrip,
    setSelectedEvent,
    handleClickSelectEvent,
    isHasSelected,
    highPassThumbnail,
    gainThumbnail,
    channelsThumbnail,
    sizeThumbnailConfig,
  };
};

export default useStripEvents;
